/* Hình ảnh khách tiềm năng: ảnh khách hàng (chân dung, tình trạng) + ảnh quá trình làm dịch vụ.
 * Thông tin ảnh lưu trong store (localStorage), file ảnh nén lưu IndexedDB (Customer360/mediaDb) — chờ nối API upload. */
import dayjs from 'dayjs';
import type { DichVu } from './logic';

export type PhotoKind = 'profile' | 'process';
export const PROFILE_TAGS = ['Chân dung', 'Tình trạng hiện tại', 'Khác'] as const;
export type ProfileTag = typeof PROFILE_TAGS[number];
/** Giai đoạn ảnh quá trình làm dịch vụ */
export const PHASES = ['Trước làm', 'Ngày làm', 'Hồi phục', 'Tái khám'] as const;
export type Phase = typeof PHASES[number];
export const PHOTO_SRC = ['Khách gửi', 'Nhân viên chụp'] as const;
export type PhotoSrc = typeof PHOTO_SRC[number];

export interface LeadPhoto {
  id: string;
  kind: PhotoKind;
  /** Ảnh khách hàng: loại ảnh · Ảnh quá trình: giai đoạn */
  tag: ProfileTag | Phase;
  dichVu?: DichVu;
  /** Ngày chụp */
  takenAt: number;
  addedAt: number;
  by: string;
  source: PhotoSrc;
  note?: string;
  w: number;
  h: number;
}

/** Khoá file ảnh trong IndexedDB */
export const mediaKey = (leadId: number, photoId: string) => `lead-${leadId}-${photoId}`;
export const MAX_BATCH = 20;

/** Ngày làm dịch vụ = ngày sớm nhất có ảnh "Ngày làm" của dịch vụ đó */
export const serviceDay = (photos: LeadPhoto[], dv?: DichVu) => {
  const days = photos.filter((p) => p.kind === 'process' && p.tag === 'Ngày làm' && p.dichVu === dv).map((p) => dayjs(p.takenAt).startOf('day').valueOf());
  return days.length ? Math.min(...days) : undefined;
};

/** Mốc so với ngày làm: "Ngày làm" · "Sau 7 ngày" · "Trước 3 ngày" */
export const dayMark = (takenAt: number, svcDay?: number) => {
  if (svcDay == null) return '';
  const n = dayjs(takenAt).startOf('day').diff(dayjs(svcDay), 'day');
  if (n === 0) return 'Ngày làm';
  return n > 0 ? `Sau ${n} ngày` : `Trước ${-n} ngày`;
};

export interface PhotoGroup { key: string; day: number; dichVu?: DichVu; mark: string; items: LeadPhoto[] }

/** Ảnh quá trình nhóm theo dịch vụ + ngày chụp, xếp theo dòng thời gian */
export function processGroups(photos: LeadPhoto[]): PhotoGroup[] {
  const map = new Map<string, PhotoGroup>();
  photos.filter((p) => p.kind === 'process').forEach((p) => {
    const day = dayjs(p.takenAt).startOf('day').valueOf();
    const key = `${p.dichVu ?? '-'}|${day}`;
    if (!map.has(key)) map.set(key, { key, day, dichVu: p.dichVu, mark: dayMark(day, serviceDay(photos, p.dichVu)), items: [] });
    map.get(key)!.items.push(p);
  });
  const phaseNo = (p: LeadPhoto) => PHASES.indexOf(p.tag as Phase);
  return Array.from(map.values())
    .map((g) => ({ ...g, items: g.items.sort((a, b) => phaseNo(a) - phaseNo(b) || a.takenAt - b.takenAt) }))
    .sort((a, b) => a.day - b.day || String(a.dichVu).localeCompare(String(b.dichVu)));
}

/** Cặp so sánh nhanh: ảnh "Trước làm" sớm nhất ↔ ảnh mới nhất sau đó của cùng dịch vụ */
export function beforeAfter(photos: LeadPhoto[], dv?: DichVu): [LeadPhoto, LeadPhoto] | null {
  const list = photos.filter((p) => p.kind === 'process' && p.dichVu === dv).sort((a, b) => a.takenAt - b.takenAt);
  const before = list.find((p) => p.tag === 'Trước làm') ?? list[0];
  const after = [...list].reverse().find((p) => p.id !== before?.id && p.tag !== 'Trước làm');
  return before && after ? [before, after] : null;
}

/** Ghi lịch sử chạm khi thêm ảnh */
export const photoLog = (n: number, kind: PhotoKind, tag: string, dv?: DichVu, takenAt?: number) => (kind === 'profile'
  ? `Thêm ${n} ảnh khách hàng · ${tag}`
  : `Thêm ${n} ảnh quá trình${dv ? ` · ${dv}` : ''} · ${tag}${takenAt ? ` · chụp ${dayjs(takenAt).format('DD/MM')}` : ''}`);
